import { useEffect } from 'react';
import { Icon } from '../utils/icons';

const OAuthResultModal = ({
    isOpen,
    onClose,
    type = 'success', // 'success' or 'error'
    title,
    message,
    service, // 'google_calendar' or 'google_sheets'
    accountInfo = null,
    autoClose = true,
    autoCloseDelay = 5000,
    onRetry = null
}) => {
    useEffect(() => {
        if (isOpen && autoClose && type === 'success') {
            const timer = setTimeout(() => {
                onClose();
            }, autoCloseDelay);

            return () => clearTimeout(timer);
        }
    }, [isOpen, autoClose, autoCloseDelay, onClose, type]);

    if (!isOpen) return null;

    const getServiceIcon = (service) => {
        if (service === 'google_calendar') {
            return <img src="/calendar_icon.svg" alt="Calendar" className="w-6 h-6" />;
        } else if (service === 'google_sheets') {
            return <Icon name="grid" size={24} className="text-green-600" />;
        }
        return <Icon name="check" size={24} className="text-blue-600" />;
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border border-gray-100 transform animate-scaleIn">
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 text-gray-400 hover:text-gray-600 transition-colors bg-white/80 backdrop-blur-sm rounded-full p-2 hover:bg-white/90"
                >
                    <Icon name="close" size={20} />
                </button>

                {/* Success Content */}
                {type === 'success' && (
                    <>
                        {/* Confetti GIF Background */}
                        <div className="relative h-40 bg-gradient-to-br from-green-50 to-blue-50 overflow-hidden">
                            <img
                                src="/successful_check_confetti.gif"
                                alt="Success celebration"
                                className="w-full h-full object-cover opacity-90"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-white/20 via-transparent to-transparent"></div>

                            {/* Success Icon Overlay */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="bg-white/95 backdrop-blur-sm rounded-full p-4 shadow-lg border border-white/50">
                                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                                        <Icon name="check" size={32} className="text-white" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Success Text Content */}
                        <div className="p-8 text-center">
                            <h3 className="text-2xl font-bold text-gray-900 mb-3 flex items-center justify-center gap-3">
                                {getServiceIcon(service)}
                                {title}
                            </h3>

                            <p className="text-gray-600 mb-6 leading-relaxed">
                                {message}
                            </p>

                            {/* Connected Account Info */}
                            {accountInfo && (
                                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                                    <div className="flex items-center justify-center space-x-3">
                                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                                            <span className="text-white text-sm font-semibold">
                                                {accountInfo.name?.[0] || 'G'}
                                            </span>
                                        </div>
                                        <div className="text-left">
                                            <p className="text-sm font-medium text-gray-900">
                                                {accountInfo.name || 'Google Account'}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                {accountInfo.email}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <button
                                onClick={onClose}
                                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2 mx-auto"
                            >
                                <Icon name="check" size={18} />
                                <span>Great!</span>
                            </button>

                            {autoClose && (
                                <p className="text-xs text-gray-400 mt-4">
                                    This will close automatically in {autoCloseDelay / 1000} seconds
                                </p>
                            )}
                        </div>
                    </>
                )}

                {/* Error Content */}
                {type === 'error' && (
                    <div className="p-8 text-center">
                        {/* Error Icon */}
                        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                            <Icon name="error" size={32} className="text-red-600" />
                        </div>

                        <h3 className="text-2xl font-bold text-gray-900 mb-3 flex items-center justify-center gap-3">
                            {getServiceIcon(service)}
                            {title}
                        </h3>

                        <p className="text-gray-600 mb-8 leading-relaxed">
                            {message}
                        </p>

                        {/* Action buttons */}
                        <div className="flex justify-center space-x-3">
                            <button
                                onClick={onClose}
                                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-3 rounded-xl font-medium transition-colors"
                            >
                                Close
                            </button>

                            {onRetry && (
                                <button
                                    onClick={() => {
                                        onClose();
                                        setTimeout(onRetry, 100);
                                    }}
                                    className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
                                >
                                    <Icon name="add" size={18} />
                                    <span>Try Again</span>
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OAuthResultModal;
